import { useMutation } from '@tanstack/react-query';

import { toast } from '@ekhodealer/ekho-common/rev';

import type { DocumentKey } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

/**
 * Download document request parameters
 */
export interface DownloadDocumentParams {
	documentKey: DocumentKey;
	orderId: string;
}

/**
 * API function to download a document
 * Downloads the file and opens it in a new tab
 */
export const downloadDocument = async (
	servicesProviderId: string,
	params: DownloadDocumentParams,
): Promise<void> => {
	const { documentKey, orderId } = params;
	const url = ordersEndpoints.downloadDocument(servicesProviderId, orderId, documentKey);
	await apiClient.downloadFile(url);
};

/**
 * Hook for downloading order documents
 * Includes error handling via toast notifications
 */
export const useDownloadDocument = () => {
	const servicesProviderId = useCurrentServicesProviderId();

	return useMutation({
		mutationFn: async (params: DownloadDocumentParams) => {
			if (!servicesProviderId) {
				throw new Error('Cannot download document without services provider ID');
			}
			return downloadDocument(servicesProviderId, params);
		},
		onError: (error) => {
			const errorMessage =
				error instanceof Error ? error.message : 'Failed to download document';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		},
	});
};
